class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeBound = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.handleCartLinkClickBound = this.handleCartLinkClick.bind(this);
    this.handleCartLinkKeydownBound = this.handleCartLinkKeydown.bind(this);
    this.handleSectionLoadBound = this.setHeaderCartIconAccessibility.bind(this);
    document.addEventListener('shopify:section:load', this.handleSectionLoadBound);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    document.removeEventListener('shopify:section:load', this.handleSectionLoadBound);
    document.addEventListener('shopify:section:load', this.handleSectionLoadBound);
    this.setHeaderCartIconAccessibility();
    this.bindOverlay();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (this.overlay === overlay) return;
    this.overlay?.removeEventListener('click', this.closeBound);
    this.overlay = overlay;
    this.overlay?.addEventListener('click', this.closeBound);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (this.cartLink === cartLink) return;

    this.releaseHeaderCartIconAccessibility();
    this.cartLink = cartLink;
    if (!this.cartLink) return;

    this.cartLinkOriginalAttributes = {
      role: this.cartLink.getAttribute('role'),
      ariaHaspopup: this.cartLink.getAttribute('aria-haspopup'),
    };
    this.cartLink.setAttribute('role', 'button');
    this.cartLink.setAttribute('aria-haspopup', 'dialog');
    this.cartLink.addEventListener('click', this.handleCartLinkClickBound);
    this.cartLink.addEventListener('keydown', this.handleCartLinkKeydownBound);
  }

  releaseHeaderCartIconAccessibility() {
    if (!this.cartLink) return;
    this.cartLink.removeEventListener('click', this.handleCartLinkClickBound);
    this.cartLink.removeEventListener('keydown', this.handleCartLinkKeydownBound);
    if (this.cartLink.isConnected !== false && this.cartLink.getAttribute('role') === 'button') {
      if (this.cartLinkOriginalAttributes?.role === null) this.cartLink.removeAttribute('role');
      else this.cartLink.setAttribute('role', this.cartLinkOriginalAttributes?.role);
    }
    if (this.cartLink.isConnected !== false && this.cartLink.getAttribute('aria-haspopup') === 'dialog') {
      if (this.cartLinkOriginalAttributes?.ariaHaspopup === null) this.cartLink.removeAttribute('aria-haspopup');
      else this.cartLink.setAttribute('aria-haspopup', this.cartLinkOriginalAttributes?.ariaHaspopup);
    }
    this.cartLink = undefined;
    this.cartLinkOriginalAttributes = undefined;
  }

  handleCartLinkClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  handleCartLinkKeydown(event) {
    if (event.code?.toUpperCase() !== 'SPACE') return;
    event.preventDefault();
    this.open(event.currentTarget);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    acquireBodyScrollLock(this);
    if (this.classList.contains('active') || this.activationTimer !== undefined) {
      this.scheduleFocusTrap();
      return;
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.activationTimer = window.setTimeout(() => {
      this.activationTimer = undefined;
      this.classList.add('animate', 'active');
    });

    this.scheduleFocusTrap();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    const ownedFocus = this.classList.contains('active')
      || this.activationTimer !== undefined
      || this.focusTrapTimer !== undefined;
    this.cancelPendingActivation();
    this.cancelPendingRenderOpen();
    this.cancelPendingFocusTrap();
    this.classList.remove('active');
    if (ownedFocus) removeTrapFocus(this.activeElement);
    releaseBodyScrollLock(this);
  }

  disconnectedCallback() {
    this.close();
    document.removeEventListener('shopify:section:load', this.handleSectionLoadBound);
    this.releaseHeaderCartIconAccessibility();
    this.overlay?.removeEventListener('click', this.closeBound);
    this.overlay = undefined;
  }

  cancelPendingActivation() {
    window.clearTimeout(this.activationTimer);
    this.activationTimer = undefined;
  }

  cancelPendingRenderOpen() {
    window.clearTimeout(this.renderOpenTimer);
    this.renderOpenTimer = undefined;
  }

  scheduleFocusTrap() {
    this.cancelPendingFocusTrap();
    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) return;
    let focusTrapped = false;
    const trapDrawerFocus = () => {
      if (focusTrapped || !this.classList.contains('active')) return;
      focusTrapped = true;
      this.cancelPendingFocusTrap();
      const focusElement = getFocusableElements(drawerInner)[0] || drawerInner;
      trapFocus(drawerInner, focusElement);
    };

    if (getMotionSafeBehavior() === 'auto') {
      this.focusTrapTimer = window.setTimeout(trapDrawerFocus);
      return;
    }

    this.focusTrapTarget = drawerInner;
    this.focusTrapHandler = trapDrawerFocus;
    drawerInner.addEventListener('transitionend', trapDrawerFocus, { once: true });
    this.focusTrapTimer = window.setTimeout(trapDrawerFocus, 500);
  }

  cancelPendingFocusTrap() {
    window.clearTimeout(this.focusTrapTimer);
    this.focusTrapTimer = undefined;
    if (this.focusTrapTarget && this.focusTrapHandler) {
      this.focusTrapTarget.removeEventListener('transitionend', this.focusTrapHandler);
    }
    this.focusTrapTarget = undefined;
    this.focusTrapHandler = undefined;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const renderPlan = this.getSectionsToRender().map((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const payload = parsedState.sections?.[section.id];
      if (!target) throw new Error(`Cart drawer destination ${section.id} is missing.`);
      if (typeof payload !== 'string') throw new Error(`Cart drawer omitted section ${section.id}.`);
      const source = this.getSectionDOM(payload, section.selector || '.shopify-section');
      if (!source) throw new Error(`Cart drawer section ${section.id} is malformed.`);
      return { target, innerHTML: source.innerHTML };
    });
    const wasActive = this.classList.contains('active');
    this.cancelPendingFocusTrap();
    if (wasActive) removeTrapFocus();
    renderPlan.forEach(({ target, innerHTML }) => { target.innerHTML = innerHTML; });
    this.overlay = undefined;
    this.bindOverlay();
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.setHeaderCartIconAccessibility();

    this.cancelPendingRenderOpen();
    if (wasActive) {
      this.scheduleFocusTrap();
      return;
    }
    this.renderOpenTimer = window.setTimeout(() => {
      this.renderOpenTimer = undefined;
      if (!this.isConnected) return;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = this.getSectionDOM(html, selector);
    if (!section) throw new Error(`Cart drawer response omitted ${selector}.`);
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
        targetSelf: true,
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
